import type { ActionRequiredWaitSegment, CRMUser, ProjectTask, TaskActivityLog } from '../types';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  getActionRequiredFullLabel,
  isActionRequiredExternal,
  type NamedUser,
} from './actionRequiredDisplay';

export const UNASSIGNED_WAIT_KEY = '__unassigned__';

export interface ActionRequiredWaitSlice {
  key: string;
  label: string;
  durationMs: number;
  percentage: number;
}

export interface ActionRequiredWaitInterval {
  key: string;
  label: string;
  startMs: number;
  endMs: number;
}

type TimelineInterval = ActionRequiredWaitInterval;

function clampMs(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function sliceKeyForHolder(holderKey: string, holderNote?: string): string {
  if (!holderKey || holderKey === UNASSIGNED_WAIT_KEY) return UNASSIGNED_WAIT_KEY;
  if (isActionRequiredExternal(holderKey)) {
    const note = (holderNote || '').trim();
    return note ? `external:${note}` : 'external';
  }
  return holderKey;
}

function labelForKey(
  key: string,
  holderNote: string | undefined,
  teamMembers: NamedUser[],
): string {
  if (key === UNASSIGNED_WAIT_KEY) return 'No action required';
  if (key.startsWith('external:')) {
    const note = key.slice('external:'.length);
    return note ? `External: ${note}` : 'External';
  }
  if (key === 'external') return 'External';
  if (isActionRequiredExternal(key)) {
    return getActionRequiredFullLabel(key, holderNote, teamMembers) || 'External';
  }
  const member = teamMembers.find((m) => m.uid === key)
    || teamMembers.find((m) => (m.email || '').trim() === key);
  return (member?.displayName || '').trim()
    || (member?.email || '').trim()
    || (member?.company || '').trim()
    || (key.includes('@') ? key : '')
    || 'Unknown';
}

function noteForKey(key: string, holderNote?: string): string | undefined {
  if (key.startsWith('external:')) return key.slice('external:'.length);
  if (isActionRequiredExternal(key)) return holderNote;
  return undefined;
}

function resolveHolderFromLogValue(
  valueKey: string | undefined,
  valueLabel: string | undefined,
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[],
): { key?: string; note?: string } {
  if (valueKey) {
    return { key: valueKey };
  }
  const label = (valueLabel || '').trim();
  if (!label || label === 'None') return {};
  if (label.startsWith('External:')) {
    const note = label.slice('External:'.length).trim();
    return { key: ACTION_REQUIRED_EXTERNAL_SENTINEL, note };
  }
  const member = teamMembers.find((m) => m.displayName === label);
  if (member) return { key: member.uid };
  return {};
}

function resolveHolderFromLog(
  log: TaskActivityLog,
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[],
): { key?: string; note?: string } {
  return resolveHolderFromLogValue(log.newValueKey, log.newValue, teamMembers);
}

function buildHolderIntervalsFromSegments(
  segments: ActionRequiredWaitSegment[],
  windowStartMs: number,
  windowEndMs: number,
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[],
): TimelineInterval[] {
  return segments
    .map((seg) => {
      const startMs = clampMs(new Date(seg.startedAt).getTime(), windowStartMs, windowEndMs);
      const endMs = clampMs(
        seg.endedAt ? new Date(seg.endedAt).getTime() : windowEndMs,
        windowStartMs,
        windowEndMs,
      );
      if (endMs <= startMs) return null;
      const key = sliceKeyForHolder(seg.holderKey, seg.holderNote);
      return {
        key,
        label: labelForKey(key, seg.holderNote, teamMembers),
        startMs,
        endMs,
      };
    })
    .filter((interval): interval is TimelineInterval => interval !== null)
    .sort((a, b) => a.startMs - b.startMs);
}

/** Prefer segment intervals on overlap; fill the rest from activity logs. */
function mergeTimelineSources(
  logIntervals: TimelineInterval[],
  segmentIntervals: TimelineInterval[],
  windowStartMs: number,
  windowEndMs: number,
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[],
): TimelineInterval[] {
  if (logIntervals.length === 0 && segmentIntervals.length === 0) return [];

  const boundaries = new Set<number>([windowStartMs, windowEndMs]);
  for (const interval of logIntervals) {
    boundaries.add(interval.startMs);
    boundaries.add(interval.endMs);
  }
  for (const interval of segmentIntervals) {
    boundaries.add(interval.startMs);
    boundaries.add(interval.endMs);
  }

  const bounds = Array.from(boundaries)
    .filter((ms) => ms >= windowStartMs && ms <= windowEndMs)
    .sort((a, b) => a - b);

  const merged: TimelineInterval[] = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const startMs = bounds[i];
    const endMs = bounds[i + 1];
    if (endMs <= startMs) continue;

    const segmentHit = segmentIntervals.find(
      (interval) => interval.startMs <= startMs && interval.endMs >= endMs,
    );
    if (segmentHit) {
      merged.push({ key: segmentHit.key, label: segmentHit.label, startMs, endMs });
      continue;
    }

    const logHit = logIntervals.find(
      (interval) => interval.startMs <= startMs && interval.endMs >= endMs,
    );
    if (logHit) {
      merged.push({ key: logHit.key, label: logHit.label, startMs, endMs });
      continue;
    }

    merged.push({
      key: UNASSIGNED_WAIT_KEY,
      label: labelForKey(UNASSIGNED_WAIT_KEY, undefined, teamMembers),
      startMs,
      endMs,
    });
  }

  return coalesceAdjacentIntervals(merged);
}

function coalesceAdjacentIntervals(intervals: TimelineInterval[]): TimelineInterval[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs);
  const out: TimelineInterval[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const prev = out[out.length - 1];
    const cur = sorted[i];
    if (cur.key === prev.key && cur.startMs <= prev.endMs) {
      prev.endMs = Math.max(prev.endMs, cur.endMs);
    } else {
      out.push({ ...cur });
    }
  }
  return out.filter((interval) => interval.endMs > interval.startMs);
}

function buildIntervalsFromActivityLogs(
  task: ProjectTask,
  activityLogs: TaskActivityLog[],
  windowStartMs: number,
  windowEndMs: number,
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[],
): TimelineInterval[] {
  const logs = activityLogs
    .filter((l) => l.taskId === task.id && l.field === 'actionRequired')
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  if (logs.length === 0) return [];

  const intervals: TimelineInterval[] = [];
  let cursor = windowStartMs;
  let current = resolveHolderFromLogValue(logs[0].oldValueKey, logs[0].oldValue, teamMembers);

  const pushInterval = (startMs: number, endMs: number, holder?: { key?: string; note?: string }) => {
    if (endMs <= startMs) return;
    if (holder?.key) {
      const key = sliceKeyForHolder(holder.key, holder.note);
      intervals.push({
        key,
        label: labelForKey(key, holder.note, teamMembers),
        startMs,
        endMs,
      });
      return;
    }
    intervals.push({
      key: UNASSIGNED_WAIT_KEY,
      label: labelForKey(UNASSIGNED_WAIT_KEY, undefined, teamMembers),
      startMs,
      endMs,
    });
  };

  for (const log of logs) {
    const at = clampMs(new Date(log.createdAt).getTime(), windowStartMs, windowEndMs);
    pushInterval(cursor, at, current.key ? current : undefined);
    current = resolveHolderFromLog(log, teamMembers);
    cursor = Math.max(cursor, at);
  }

  if (cursor < windowEndMs) {
    const tailHolder = current.key
      ? current
      : task.actionRequired
        ? { key: task.actionRequired, note: task.actionRequiredNote }
        : undefined;
    pushInterval(cursor, windowEndMs, tailHolder);
  }

  return intervals.filter((i) => i.endMs > i.startMs);
}

function aggregateIntervals(intervals: TimelineInterval[], totalMs: number): ActionRequiredWaitSlice[] {
  const totals = new Map<string, { label: string; durationMs: number }>();
  for (const interval of intervals) {
    const durationMs = interval.endMs - interval.startMs;
    if (durationMs <= 0) continue;
    const prev = totals.get(interval.key);
    if (prev) {
      prev.durationMs += durationMs;
    } else {
      totals.set(interval.key, { label: interval.label, durationMs });
    }
  }

  const slices = Array.from(totals.entries())
    .map(([key, value]) => ({
      key,
      label: value.label,
      durationMs: value.durationMs,
      percentage: totalMs > 0 ? (value.durationMs / totalMs) * 100 : 0,
    }))
    .sort((a, b) => b.durationMs - a.durationMs);

  return slices;
}

export function getActionRequiredWaitWindow(task: ProjectTask, now = new Date()): { startMs: number; endMs: number; totalMs: number } {
  const startMs = new Date(task.createdAt).getTime();
  const endMs = task.completedAt ? new Date(task.completedAt).getTime() : now.getTime();
  const totalMs = Math.max(0, endMs - startMs);
  return { startMs, endMs, totalMs };
}

export function getActionRequiredWaitIntervals(
  task: ProjectTask,
  activityLogs: TaskActivityLog[],
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[],
  now = new Date(),
): ActionRequiredWaitInterval[] {
  const { startMs, endMs, totalMs } = getActionRequiredWaitWindow(task, now);
  if (totalMs <= 0) return [];

  const segments = task.actionRequiredWaitSegments;
  const logIntervals = buildIntervalsFromActivityLogs(task, activityLogs, startMs, endMs, teamMembers);
  const segmentIntervals = segments?.length
    ? buildHolderIntervalsFromSegments(segments, startMs, endMs, teamMembers)
    : [];
  const intervals = mergeTimelineSources(
    logIntervals,
    segmentIntervals,
    startMs,
    endMs,
    teamMembers,
  );

  if (intervals.length === 0) {
    if (task.actionRequired) {
      const key = sliceKeyForHolder(task.actionRequired, task.actionRequiredNote);
      return [{
        key,
        label: labelForKey(key, task.actionRequiredNote, teamMembers),
        startMs,
        endMs,
      }];
    }
    return [{
      key: UNASSIGNED_WAIT_KEY,
      label: labelForKey(UNASSIGNED_WAIT_KEY, undefined, teamMembers),
      startMs,
      endMs,
    }];
  }

  return intervals;
}

export function computeActionRequiredWaitBreakdown(
  task: ProjectTask,
  activityLogs: TaskActivityLog[],
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[],
  now = new Date(),
): ActionRequiredWaitSlice[] {
  const { totalMs } = getActionRequiredWaitWindow(task, now);
  if (totalMs <= 0) return [];

  const intervals = getActionRequiredWaitIntervals(task, activityLogs, teamMembers, now);
  return aggregateIntervals(intervals, totalMs);
}

export function formatWaitDuration(durationMs: number): string {
  const mins = Math.floor(durationMs / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
}

export function holderNoteFromSliceKey(key: string): string | undefined {
  return noteForKey(key);
}
